import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaUser, FaCog, FaMobileAlt, FaSignOutAlt, FaSignInAlt, FaShoppingCart, FaChevronRight } from "react-icons/fa";
import { cartContext } from "./CartContext";
import { FetchNestedCategories } from "../Data.js";
import HeaderSearchInput from "./HeaderSearchInput";

const Header = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isDropdownLocked, setIsDropdownLocked] = useState(false); // Prevents auto-hide when clicked
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const [username, setUsername] = useState(null);
  const [nestedCategories, setNestedCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [selectedSubModule, setSelectedSubModule] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [dashboardModules, setDashboardModules] = useState([]);

  const { cartCount, GetCartItemsAsync } = useContext(cartContext);
  const navigate = useNavigate();



  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchModules = async () => {
    // Removed unused function
  };

  const fetchSubcategories = async (subModuleID) => {
    // Removed unused function
  };

  const fetchSubModuleCategories = async (subModuleID) => {
    try {
      // This would typically fetch from an API endpoint
      // For now, return empty array to prevent errors
      return [];
    } catch (error) {
      console.error("Error fetching sub-module categories:", error);
      return [];
    }
  };

  // Get Nested Categories
  const getNestedCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await FetchNestedCategories();
      console.log('Raw categories response:', response); // Debug log
      
      if (!response || !Array.isArray(response)) {
        console.warn('No categories received or invalid format:', response);
        setNestedCategories([]);
        setCategoriesLoading(false);
        return;
      }

      // Add showAll property to each category for toggling
      const categoriesWithToggle = response.map(category => ({
        ...category,
        showAll: false,
        subCategories: (category.subCategories || []).map(subCategory => ({
          ...subCategory,
          showAll: false
        }))
      }));
      console.log('Processed categories:', categoriesWithToggle); // Debug log
      setNestedCategories(categoriesWithToggle);
      setCategoriesLoading(false);
    } catch (error) {
      console.error("Error fetching nested categories:", error);
      setNestedCategories([]); // Set empty array on error to show loading state
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    getNestedCategories();
    GetCartItemsAsync();
  }, [GetCartItemsAsync]);

  const showDropdown = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setIsDropdownVisible(true);
  };

  const hideDropdown = () => {
    if (!isDropdownLocked) {
      const timeout = setTimeout(() => {
        setIsDropdownVisible(false);
      }, 300); // Add 300ms delay to allow users to move mouse to dropdown
      setDropdownTimeout(timeout);
    }
  };

  const toggleDropdown = () => {
    if (isDropdownVisible) {
      setIsDropdownVisible(false);
      setIsDropdownLocked(false);
    } else {
      setIsDropdownVisible(true);
      setIsDropdownLocked(true); // Lock when clicked to prevent auto-hide
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownVisible && 
          !event.target.closest('.account-dropdown') && 
          !event.target.closest('.mobile-dropdown') &&
          !event.target.closest('.mobile-dropdown-trigger')) {
        setIsDropdownVisible(false);
        setIsDropdownLocked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [isDropdownVisible, dropdownTimeout]);

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const handleSubModuleClick = async (subModule) => {
    try {
      const subCategories = await fetchSubModuleCategories(subModule.subModuleID);
  
      if (subCategories.length > 0) {
        setSelectedSubModule(subModule);
        setSubCategories(subCategories);
      } else {
        navigate(subModule.subModuleUrl);
        setSidebarOpen(false);
      }
    } catch (error) {
      console.error("Error handling submodule click:", error);
    }
  };

  const handleCategoryClick = (category) => {
    if (category.subCategories && category.subCategories.length > 0) {
      setSelectedCategory(category);
    } else {
      // Navigate to category page if no subcategories
      navigate(`/category/${category.slug}`);
      setSidebarOpen(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setNestedCategories(prevCategories =>
      prevCategories.map(category =>
        category.categoryId === categoryId
          ? { ...category, showAll: !category.showAll }
          : category
      )
    );
  };

  const HandleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userID");
    localStorage.removeItem("username");
    localStorage.removeItem("userRole");
    setUsername(null);
  };

  const toggleSubmodules = (moduleId) => {
    setDashboardModules(prevModules =>
      prevModules.map(module =>
        module.moduleID === moduleId
          ? { ...module, showAll: !module.showAll }
          : module
      )
    );
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  return (
    <>
      {/* Header */}
      <header className="bg-blue-600 text-white">
        {/* Top Header */}
        <div className="container mx-auto px-4 py-2 relative">
          {/* First Row - Logo and Right Items */}
          <div className="flex items-center justify-between pb-2">
            {/* Left Section - Logo and Menu */}
            <div className="flex items-center space-x-4">
              <button 
                className="flex items-center hover:underline md:hidden"
                onClick={toggleSidebar}
              >
                <FaBars className="mr-1" />
              </button>
              <div className="flex items-center space-x-2">
                <div className="bg-white p-1 rounded shadow-md">
                  <img
                    src="https://minimartke-products-upload.s3.us-east-1.amazonaws.com/product-images/e4dabde0-e9b7-4b2c-b397-247b659efc13.png"
                    alt="Quickcrate Express Logo"
                    className="w-8 h-8"
                  />
                </div>
                <span className="font-semibold">Quickcrate Express</span>
              </div>
            </div>

            {/* Center Section - Search (hidden on mobile) */}
            {!isMobileView && (
              <div className="flex-1 max-w-xl mx-4">
                <HeaderSearchInput className="w-full" />
              </div>
            )}

            {/* Right Section - Navigation */}
            <div className="flex items-center space-x-6">
            {/* Account Dropdown */}
            <div 
              className="relative group hidden md:block account-dropdown"
              onMouseEnter={showDropdown}
              onMouseLeave={hideDropdown}
            >
              <div 
                className="flex flex-col items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 transition-colors"
                onClick={toggleDropdown}
              >
                <span className="text-xs">
                  {username ? `Hello, ${username}` : "Hello"}
                </span>
                <span className="text-sm font-semibold">Accounts</span>
              </div>
              
              {isDropdownVisible && (
                <div 
                  className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-50 text-gray-800 border border-gray-200"
                  onMouseEnter={showDropdown}
                  onMouseLeave={hideDropdown}
                >
                  <div className="py-1">
                    {username ? (
                      <>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors"
                          onClick={() => {
                            setIsDropdownVisible(false);
                            setIsDropdownLocked(false);
                          }}
                        >
                          <FaUser className="mr-2" /> My Profile
                        </Link>
                        <Link
                          to="/account-settings"
                          className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors"
                          onClick={() => {
                            setIsDropdownVisible(false);
                            setIsDropdownLocked(false);
                          }}
                        >
                          <FaCog className="mr-2" /> Account Settings
                        </Link>
                        <Link
                          to="/device-management"
                          className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors"
                          onClick={() => {
                            setIsDropdownVisible(false);
                            setIsDropdownLocked(false);
                          }}
                        >
                          <FaMobileAlt className="mr-2" /> Device Management
                        </Link>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => {
                            HandleLogout();
                            setIsDropdownVisible(false);
                            setIsDropdownLocked(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors text-red-600"
                        >
                          <FaSignOutAlt className="mr-2" /> Sign Out
                        </button>
                      </>
                    ) : (
                      <Link
                        to="/Login"
                        className="block px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors"
                        onClick={() => {
                          setIsDropdownVisible(false);
                          setIsDropdownLocked(false);
                        }}
                      >
                        <FaSignInAlt className="mr-2" /> Sign In
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>              {/* Returns & Orders */}
              <div 
                className="flex flex-col items-center cursor-pointer hidden md:flex"
                onClick={() => navigate("/ReturnsAndOrdersPage")}
              >
                <span className="text-xs">Returns</span>
                <span className="text-sm font-semibold">& Orders</span>
              </div>

              {/* Cart */}
              <div 
                className="flex items-center cursor-pointer relative"
                onClick={() => navigate("/ProductPage")}
              >
                <div className="relative">
                  <FaShoppingCart className="text-2xl" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </div>
                <span className="ml-1 text-sm font-semibold hidden md:block">Cart</span>
              </div>

              {/* Mobile Profile Dropdown Trigger */}
              <button 
                onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                className="flex items-center md:hidden mobile-dropdown-trigger"
              >
                <FaUser className="text-xl" />
              </button>
            </div>
          </div>

          {/* Second Row - Search Bar (only on mobile) */}
          {isMobileView && (
            <div className="w-full pb-2">
              <HeaderSearchInput isMobile={true} className="w-full" />
            </div>
          )}
          


          {/* Mobile Dropdown */}
          {isMobileView && isDropdownVisible && (
            <div className="absolute left-0 right-0 top-full bg-white shadow-lg z-[60] text-gray-800 border-b border-gray-200 mobile-dropdown">
              <div className="py-2">
                <div className="px-4 py-2 font-medium border-b border-gray-200">
                  {username ? `Hello, ${username}` : "Hello, Sign In"}
                </div>
                
                <div className="py-1">
                  <h4 className="px-4 py-2 font-medium">Account</h4>
                  {username ? (
                    <>
                      <Link
                        to="/profile"
                        className="block px-6 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                        onClick={() => setIsDropdownVisible(false)}
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/account-settings"
                        className="block px-6 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                        onClick={() => setIsDropdownVisible(false)}
                      >
                        Account Settings
                      </Link>
                      <button
                        onClick={() => {
                          HandleLogout();
                          setIsDropdownVisible(false);
                        }}
                        className="w-full text-left px-6 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/Login"
                      className="block px-6 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                      onClick={() => setIsDropdownVisible(false)}
                    >
                      Sign In
                    </Link>
                  )}
                </div>
                
                <div className="border-t border-gray-200"></div>
                
                <div className="py-1">
                  <h4 className="px-4 py-2 font-medium">Orders</h4>
                  <button
                    onClick={() => {
                      navigate("/ReturnsAndOrdersPage");
                      setIsDropdownVisible(false);
                    }}
                    className="w-full text-left px-6 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                  >
                    Returns & Orders
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sub Header */}
        <div className="bg-blue-400">
          <div className="container mx-auto px-4 py-2 flex items-center">
            {/* Sidebar Toggle (hidden on mobile) */}
            <button 
              className="hidden md:flex items-center mr-4 hover:underline"
              onClick={toggleSidebar}
            >
              <FaBars className="mr-1" />
              <span>All</span>
            </button>

            {/* Navigation Links */}
            <div className="flex space-x-4">
              <button className="hover:underline">
                Great Deals
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Amazon-Style Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-80 bg-white shadow-xl z-50 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out`}>
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="bg-gray-800 text-white px-4 py-4 flex justify-between items-center">
            <div className="flex items-center">
              <FaUser className="mr-2" />
              <span className="font-medium">
                {username ? `Hello, ${username}` : "Hello, Sign in"}
              </span>
            </div>
            <button 
              onClick={toggleSidebar}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Shop by Department Header */}
            {/* <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-bold text-lg text-gray-800">Shop by Department</h3>
            </div> */}

            {selectedCategory ? (
              /* Selected Category View - Show Subcategories */
              <div className="p-4">
                <button 
                  onClick={() => setSelectedCategory(null)}
                  className="text-orange-600 hover:text-orange-800 mb-4 flex items-center font-medium"
                >
                  <span className="mr-1">←</span> Main Menu
                </button>
                
                <div className="mb-6">
                  <h4 className="font-bold text-lg mb-3 text-gray-800">{selectedCategory.name}</h4>
                  
                  {/* "See All" option for the main category */}
                  <div className="mb-4">
                    <Link 
                      to={`/category/${selectedCategory.slug || selectedCategory.categoryName.toLowerCase().replace(/\s+/g, '-')}`}
                      className="block py-2 px-3 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 font-medium transition-colors"
                      onClick={() => setSidebarOpen(false)}
                    >
                      See All {selectedCategory.name}
                    </Link>
                  </div>

                  {/* Subcategories */}
                  {selectedCategory.subCategories && selectedCategory.subCategories.length > 0 && (
                    <div className="border-t pt-3">
                      <div className="space-y-1">
                        {selectedCategory.subCategories.map((subCategory) => (
                          <Link 
                            key={subCategory.subCategoryId}
                            // to={`/subcategory/${subCategory.slug || subCategory.name.toLowerCase().replace(/\s+/g, '-')}`}
                            to={`/subcategory/${subCategory.subCategoryId}`}
                            className="block py-2 px-3 text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            onClick={() => setSidebarOpen(false)}
                          >
                            {subCategory.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Main Categories List */
              <div>
                {/* Shop by Department Header */}
                {/* <div className="bg-gray-50 px-4 py-3 border-b">
                  <h3 className="font-bold text-lg text-gray-800">Shop by Department</h3>
                </div> */}
                
                {/* Show loading state if categories are loading */}
                {categoriesLoading ? (
                  <div className="p-6 text-center">
                    <div className="text-gray-600">Loading categories...</div>
                    <div className="mt-2 text-sm text-gray-500">Please wait while we load the departments</div>
                  </div>
                ) : nestedCategories && nestedCategories.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {nestedCategories.map((category) => (
                      <div key={category.categoryId} className="py-2">
                        <button
                          onClick={() => handleCategoryClick(category)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 flex justify-between items-center transition-colors"
                        >
                          <span className="font-medium text-gray-800 text-base">{category.name}</span>
                          {category.subCategories && category.subCategories.length > 0 && (
                            <FaChevronRight className="text-gray-400 text-sm" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* No categories available */
                  <div className="p-6 text-center">
                    <div className="text-gray-600">No categories available</div>
                    <div className="mt-2 text-sm text-gray-500">Please check your internet connection</div>
                    <button 
                      onClick={getNestedCategories}
                      className="mt-3 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Additional Quick Links Section - Only show after categories are loaded */}
                {!categoriesLoading && nestedCategories && nestedCategories.length > 0 && (
                  <div className="border-t border-gray-300 mt-4">
                    <div className="bg-gray-50 px-4 py-3">
                      <h4 className="font-bold text-md text-gray-800">Help & Settings</h4>
                    </div>
                    <div className="py-2">
                      <Link to="/account" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors">
                        Your Account
                      </Link>
                      <Link to="/orders" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors">
                        Your Orders
                      </Link>
                      <Link to="/wishlist" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors">
                        Your Wish List
                      </Link>
                      <Link to="/customer-service" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors">
                        Customer Service
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div> 

      

      {/* Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Header;